"use client";

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { type Field, type LinkField } from "@sitecore-content-sdk/nextjs";
import Button from "../../atoms/Button";
import Icon from "../../atoms/Icon";
import { ICONS } from "../../variables/icons";
import RichTextWithTable from "../../molecules/RichTextWithTable/RichTextWithTables";

interface ModalDialogProps {
  modalIdentifier?: string;
  title?: Field<string>;
  footer?: Field<string>;
  sizeClass: string;
  positionClass: string;
  spacingClassNames: string;
  buttonStyle: string;
  buttonPositionClass: string;
  hiddenBlockStyles: string;
  children: ReactNode;
}

declare global {
  interface Window {
    openModal?: () => void;
    hideModal?: () => void;
  }
}

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "area[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  '[tabindex]:not([tabindex="-1"])',
  "[contenteditable]",
].join(", ");

const ANIMATION_MS = 300;

const ModalDialog = ({
  modalIdentifier,
  title,
  footer,
  sizeClass,
  positionClass,
  spacingClassNames,
  buttonStyle,
  buttonPositionClass,
  hiddenBlockStyles,
  children,
}: ModalDialogProps) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Synthesised LinkField so the shared <Button> atom can render a trigger
  // labelled with the ModalIdentifier value.
  const buttonField: LinkField = {
    value: {
      href: "/",
      title: modalIdentifier,
      text: modalIdentifier,
    },
  };

  const closeModal = () => {
    setIsAnimating(false);
    setTimeout(() => setIsOpen(false), ANIMATION_MS);
  };

  const openModal = () => {
    setIsOpen(true);
    setTimeout(() => setIsAnimating(true), 10);
  };

  // Show/hide native <dialog>, lock body scroll, and tag external links
  // inside the modal so they open in a new tab.
  useEffect(() => {
    const dialog = dialogRef.current;
    const html = document.documentElement;
    if (!dialog) return;

    if (!isOpen) {
      html.classList.remove("no-scroll");
      dialog.close();
      return;
    }

    dialog.showModal();
    html.classList.add("no-scroll");
    dialog.focus();

    const annotateExternalLinks = () => {
      const links = dialog.querySelectorAll("a[href]");
      links.forEach((link) => {
        const href = link.getAttribute("href");
        if (href && /^https?:\/\//.test(href)) {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        }
      });
    };

    const observer = new MutationObserver(annotateExternalLinks);
    observer.observe(dialog, { childList: true, subtree: true });
    annotateExternalLinks();

    return () => {
      observer.disconnect();
      html.classList.remove("no-scroll");
    };
  }, [isOpen]);

  // Trap focus within the dialog while it's open.
  useEffect(() => {
    if (!isOpen) return;

    const trapFocus = (event: globalThis.KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog || event.key !== "Tab") return;

      const focusable =
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", trapFocus);
    return () => document.removeEventListener("keydown", trapFocus);
  }, [isOpen]);

  // Expose imperative openers globally so authoring code can trigger by name.
  // NOTE: this is a global singleton — multiple Modal instances will overwrite
  // each other. If multiple modals on the same page need to be triggered
  // independently, key by ModalIdentifier instead.
  useEffect(() => {
    window.openModal = openModal;
    window.hideModal = closeModal;
  }, []);

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === "Escape") closeModal();
  };

  return (
    <div
      className={`modal-component ${hiddenBlockStyles}`}
      data-role="modal-component"
    >
      <Button
        buttonType={buttonStyle}
        iconName="CHEVRON_BLUE_RIGHT"
        field={buttonField}
        className={`cursor-pointer ${buttonPositionClass}`}
        onClickCustom={openModal}
        data-testid="modal-open-button"
      />
      {isOpen && (
        <dialog
          ref={dialogRef}
          data-testid="modal-dialog"
          className={`
            fixed z-50 w-[90%] max-w-[520px]
            ${positionClass}
            ${spacingClassNames}
            ${sizeClass}
            rounded-md shadow-lg border-none outline-none
            transition-all duration-300 ease-in-out
            ${isAnimating ? "translate-y-[10%] opacity-100" : "-translate-y-full opacity-0"}
          `}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onKeyDown={handleDialogKeyDown}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="max-h-[80vh] overflow-y-auto">
            <div
              className={`bg-white p-6 md:p-8 relative rounded-md
                transition-all duration-300 ease-in-out
                ${isAnimating ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-10"}`}
            >
              <button
                type="button"
                className="absolute top-4 right-4 text-black hover:text-gray-600 cursor-pointer"
                onClick={closeModal}
                aria-label="Close modal"
                data-testid="close-button"
              >
                <Icon icon={ICONS.DISMISS} />
              </button>

              {title?.value && (
                <h2 id="modal-title" className="text-xl font-semibold mb-4">
                  <RichTextWithTable
                    className="modal-header rte-content"
                    field={title}
                  />
                </h2>
              )}

              <div className="mb-4" data-testid="modal-content">
                {children}
              </div>

              {footer?.value && (
                <div
                  className="pt-4 border-t border-gray-200 flex gap-2 "
                  data-testid="modal-footer"
                >
                  <RichTextWithTable
                    className="modal-footer rte-content"
                    field={footer}
                  />
                </div>
              )}
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default ModalDialog;
